import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TableCell, TableRow, Typography } from "@mui/material";

import messages from "../../../assets/locale/messages";
import {
  checkForMultiline,
  formatDate
} from "../../../utils/Helpers";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import ViewDeleteButton from "../../../components/ViewDeleteButton";
import ViewEditButton from "../../../components/ViewEditButton";
import Pagination from "../../../components/Pagination";
import SearchInput from "../../../components/SearchInput";
import Table from "../../../components/Table";
import EmptyState from "../../../components/EmptyState";
import TruncateText from "../../../components/TruncateText";
import noProductsListImg from "../../../assets/images/placeholders/noProductListPlaceholder.svg";
import noRequestImg from "../../../assets/images/placeholders/noProductImagePlacholder.svg";
import {
  getSubCategoryDetailsRequest,
  getSubCategoryDetailsResponse,
  subCategoryProductsListRequest,
  subCategoryProductsListResponse
} from "../../../store/SubCategories/actions";
import { countriesLookupRequest } from "../../../store/Lookups/actions";
import Filters from "../../../components/Filters";
import ListFilter from "../../../components/Filters/ListFilter";
import SortList from "../../../components/Filters/SortList";
import DeleteSubCategory from "../DeleteSubCategory";
import { SUB_CATEGORY_PRODUCTS_SORTING } from "../../../utils/Constants";
import { hasPermission, PERMISSIONS_IDS } from "../../../utils/Roles";
import "./SubCategoryDetails.scss";

const SubCategoryDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isLoading = useSelector((state) => state.loader.showLoader);
  const lang = useSelector((state) => state.locale.lang);
  const subCategoryDetails = useSelector(
    (state) => state.subCategories.subCategoryDetails
  );
  const subCategoryProductsList = useSelector(
    (state) => state.subCategories.subCategoryProducts.list
  );
  const subCategoryProductsMeta = useSelector(
    (state) => state.subCategories.subCategoryProducts.meta
  );
  const countriesLookup = useSelector(
    (state) => state.lookups.countries.list
  );

  const { subCategories } = messages[lang];

  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [sortingRange, setSortingRange] = useState(
    SUB_CATEGORY_PRODUCTS_SORTING[0]
  );
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [deletedSubCategoryDetails, setDeletedSubCategoryDetails] =
    useState({});
  const [deleteSubCategoryModalOpen, setDeleteSubCategoryModalOpen] =
    useState(false);
  const [countryFilterValue, setCountryFilterValue] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [isApplyFilters, setIsApplyFilters] = useState(false);
  const [paramsObject, setParamsObject] = useState({
    country_ids: []
  });

  useEffect(() => {
    dispatch(countriesLookupRequest());
    return () => {
      dispatch(getSubCategoryDetailsResponse({}));
      dispatch(
        subCategoryProductsListResponse({
          list: [],
          meta: {}
        })
      );
    };
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(
        getSubCategoryDetailsRequest({
          id
        })
      );
    }
  }, [id]);

  const handleEditSubCategory = () => {
    navigate(ROUTE_PATHS.editSubCategories.replace(":id", id));
  };

  const handleDeleteSubCategory = () => {
    setDeletedSubCategoryDetails({
      id,
      name: subCategoryDetails.Name,
      src: ROUTE_PATHS.subcategoryDetails
    });
    setDeleteSubCategoryModalOpen(true);
  };

  const handleCloseModal = () => {
    setDeleteSubCategoryModalOpen(false);
    setDeletedSubCategoryDetails({});
  };

  const onImageError = (e) => {
    e.target.src = noRequestImg;
  };

  const requestSubCategoryProductsList = () => {
    if (id)
      dispatch(
        subCategoryProductsListRequest({
          params: {
            subCategoryId: id,
            page,
            search_value: searchValue,
            orderByMostRecent: sortingRange.value,
            ...paramsObject
          }
        })
      );
  };

  useEffect(() => {
    requestSubCategoryProductsList();
  }, [page, searchValue, sortingRange]);

  useEffect(() => {
    if (isApplyFilters) {
      setParamsObject({
        country_ids: countryFilterValue
      });
      setIsApplyFilters(false);
    }
  }, [isApplyFilters]);

  useEffect(() => {
    page > 1 ? setPage(1) : requestSubCategoryProductsList();
  }, [paramsObject]);

  useEffect(() => {
    if (clearFilters) {
      setCountryFilterValue([]);
      setParamsObject({
        country_ids: []
      });
    }
  }, [clearFilters]);

  const renderFilterContent = () => (
    <div className="filter-content-container h-100">
      <div className="border-bottom py-3 px-md-5 px-3">
        <ListFilter
          list={countriesLookup}
          name="countries"
          label={subCategories.subCategoryDetails.filter.countries}
          searchPlaceholder={
            subCategories.subCategoryDetails.filter.searchPlaceholders
          }
          selectedOptionsIds={countryFilterValue}
          setSelectedOptionsIds={setCountryFilterValue}
          clearFilters={clearFilters}
          collabsible
          showSearchBar
          filterModalOpen={filterModalOpen}
        />
      </div>
    </div>
  );

  const renderProductsTableRow = ({
    Id,
    Name,
    DefaultImage,
    ExporterName,
    Country,
    PublishedDate
  }) => (
    <TableRow
      key={`request-row-${Id}`}
      onClick={() => {
        navigate(
          ROUTE_PATHS.productDetailsApproved.replace(":id", Id)
        );
      }}>
      <TableCell>
        <div className="d-flex align-items-center gap-3 pointer">
          <img
            src={DefaultImage || noRequestImg}
            onError={onImageError}
            alt="subCategoriesImage"
            className="sub-category-img faint-grey-border border-radius-8"
          />
          <TruncateText text={Name || "__"} />
        </div>
      </TableCell>
      <TableCell>
        <TruncateText text={ExporterName || "__"} />
      </TableCell>
      <TableCell>
        <TruncateText text={Country || "__"} />
      </TableCell>
      <TableCell>
        {PublishedDate ? formatDate(PublishedDate) : "__"}
      </TableCell>
    </TableRow>
  );

  return (
    <div className="approved-sub-category-details-container d-flex flex-column pb-4">
      <div className="page-title-buttons-container pb-4 d-flex justify-content-between align-items-center">
        <div className="page-title">
          <Typography variant="h5">
            {subCategories.subCategoryDetails.subCategoryDetailsTitle}
          </Typography>
        </div>
        <div className="d-flex gap-3">
          {hasPermission(PERMISSIONS_IDS.sub_category_edit) && (
            <ViewEditButton onClick={handleEditSubCategory} />
          )}
          {hasPermission(PERMISSIONS_IDS.sub_category_delete) && (
            <ViewDeleteButton onClick={handleDeleteSubCategory} />
          )}
        </div>
      </div>
      <div className="sub-category-details-container bg-white d-flex w-100 p-4 col-12 gap-3  border-radius-8">
        <div className="img-container col-xl-2 col-lg-3 col-md-3 col-12">
          <img
            src={subCategoryDetails.AdminCoverImage}
            alt={subCategoryDetails.OriginalAdminCoverImageName}
            onError={onImageError}
            className="faint-grey-border"
          />
        </div>
        <div className="sub-category-details d-flex flex-column justify-content-start col-xl-8 col-lg-8 col-md-9 col-12">
          <Typography className="text-light-grey mb-0">
            {subCategoryDetails.CategoryName}
          </Typography>
          <Typography className="fsize-16 fweight-500 pb-2">
            {subCategoryDetails.Name}
          </Typography>
          <Typography variant="span" className="text-secondary">
            {subCategoryDetails.Description
              ? checkForMultiline(subCategoryDetails.Description)
              : ""}
          </Typography>
        </div>
      </div>
      <div className="sub-categroy-products-list-container">
        <div className="page-title pb-4 d-flex justify-content-between align-items-center mt-3">
          <div className="title-count-container d-flex justify-content-center align-items-center">
            <div>
              <Typography variant="h6">
                {subCategories.subCategoryDetails.products}
              </Typography>
            </div>
            <div className="block-title text-secondary">
              <div className="block-title text-secondary ps-1 ">
                <Typography>{`(${subCategoryProductsMeta?.count ?? "0"} ${subCategoryProductsMeta?.count === 1 ? subCategories.subCategoryDetails.product : subCategories.subCategoryDetails.products})`}</Typography>
              </div>
            </div>
          </div>
          <div className="title-buttons-container d-flex justify-content-center gap-3 align-items-center">
            <SortList
              options={SUB_CATEGORY_PRODUCTS_SORTING}
              onSelectOption={(selectedOption) => {
                setPage(1);
                setSortingRange(selectedOption);
              }}
              defaultOption={0}
              selectedOption={sortingRange}
            />
            <Filters
              handleClearFilters={setClearFilters}
              handleApplyFilters={setIsApplyFilters}
              FilterContent={renderFilterContent()}
              setIsModalOpen={(isOpen) => setFilterModalOpen(isOpen)}
            />
          </div>
        </div>
        <div className="products-table-container bg-white d-flex flex-column w-100  p-4 pb-0 px-0 border-radius-8">
          <div className="table-header d-flex px-4 align-items-center w-100 mb-4">
            <SearchInput
              name="products-Search"
              id="products-Search"
              value={searchValue}
              inputClass="border-radius-8"
              inputWrapperClass=" search-input-wrapper search-input-wrapper-width medium-placeholder-search"
              placeholder={
                subCategories.subCategoryDetails.searchPlaceholder
              }
              onChange={(value) => {
                setPage(1);
                setSearchValue(value);
              }}
            />
          </div>
          <div className="table-content">
            <div>
              {subCategoryProductsList?.length ? (
                <Table
                  className={"px-0"}
                  headlines={[
                    subCategories.subCategoryDetails.tableHeader
                      .product,
                    subCategories.subCategoryDetails.tableHeader
                      .exporterName,
                    subCategories.subCategoryDetails.tableHeader
                      .country,
                    subCategories.subCategoryDetails.tableHeader
                      .publishingDate
                  ]}
                  rows={
                    !isLoading &&
                    subCategoryProductsList &&
                    subCategoryProductsList.map((product) =>
                      renderProductsTableRow(product)
                    )
                  }
                />
              ) : (
                <div className="border-top">
                  {!isLoading && (
                    <EmptyState
                      subTitle={
                        !!searchValue &&
                        !subCategoryProductsList.length
                          ? subCategories.list.noSearchResults
                          : subCategories.subCategoryDetails
                              .noProductsFound
                      }
                      buttonClassName="empty-state-create-button border-radius-8 w-auto"
                      imgSrc={noProductsListImg}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="Pagination-container d-flex justify-content-center py-3 mt-2">
            {!isLoading && subCategoryProductsList?.length > 0 && (
              <Pagination
                count={subCategoryProductsMeta?.pages}
                page={page || subCategoryProductsMeta?.currentPage}
                handleChange={(page) => {
                  setPage(page);
                }}
                defaultPage={1}
              />
            )}
          </div>
        </div>
      </div>
      {deleteSubCategoryModalOpen && (
        <DeleteSubCategory
          open={deleteSubCategoryModalOpen}
          closeModal={handleCloseModal}
          subCategoryInfo={deletedSubCategoryDetails}
        />
      )}
    </div>
  );
};

export default SubCategoryDetails;
